import {
  IGenerateOfferLetterResponse,
  IsalaryDetail,
  ISalaryPageStates,
} from '@/interfaces/employee.model';
import {
  calculateSalaryValues,
  handleFormatAmount,
  showToastNotification,
} from '@/utility/helper';
import Button from '../ui/Button';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import EditSalaryModal from '../Modal/EditSalaryModal';
import EditIcon from '../Icons/EditIcon';
import { useState } from 'react';
import { IDocument } from '@/interfaces/user.model';
interface ISalaryDetailsProps {
  salaryDetail: IsalaryDetail;
  toggleModal: () => void;
  setSelectedDocument: React.Dispatch<
    React.SetStateAction<Partial<IDocument> | null>
  >;
  pageStates: ISalaryPageStates;
  setPageStates: React.Dispatch<React.SetStateAction<ISalaryPageStates>>;
  empId: string | null;
  getEmployeeDetail: () => Promise<void>;
}
const SalaryDetailsAndEdit = ({
  salaryDetail,
  toggleModal,
  setSelectedDocument,
  setPageStates,
  empId,
  pageStates,
  getEmployeeDetail,
}: ISalaryDetailsProps) => {
  const [salaryModal, setSalaryModal] = useState<boolean>(false);
  const dataHead = [
    'Allowances',
    'Deductions',
    'Is Fixed',
    'Is Condition Based',
    'Rate',
    'Based On',
    'Amount (₹)',
  ];
  const toggleSalaryModal = () => {
    setSalaryModal(!salaryModal);
  };
  const {
    grossIncome,
    totalEarningsMonthly,
    ctc,
    totalDeductionsMonthly,
    totalFromEmployerMonthly,
    basicSalary,
    grossAllowances,
    netSalary,
  } = calculateSalaryValues(salaryDetail);
  const handleGenerateOfferLetter = async () => {
    setPageStates((pre) => ({
      ...pre,
      isLoadingOfferLetter: true,
    }));
    try {
      const response: IGenerateOfferLetterResponse = await callApi(
        API_URL + API_ENDPOINT.getOfferLetter + `?userId=${empId}`,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        setSelectedDocument({ url: response?.data });
        toggleModal();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        isLoadingOfferLetter: false,
      }));
    }
  };
  return (
    <>
      <div className="row">
        <div className="col-12">
          <h4 className="fs-3 rajdhani-semibold my-4 border-bottom pb-3 d-flex align-items-center justify-content-between">
            <strong>Salary Structure</strong>
            <Button
              className="btn btn-primary"
              type="button"
              onClick={toggleSalaryModal}
            >
              <EditIcon />
              Edit Salary
            </Button>
          </h4>
        </div>
        <EditSalaryModal
          isOpen={salaryModal}
          toggle={toggleSalaryModal}
          setPageStates={setPageStates}
          empId={empId}
          getEmployeeDetail={getEmployeeDetail}
        />
        <div className="col-12 mb-5">
          <div className="card shadow-sm rounded-4 px-4">
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="border rounded-3 p-3 bg-light text-center">
                  <h4 className="text-muted mb-1">Daily Wages</h4>
                  <h4 className="text-success fw-bold">
                    {handleFormatAmount(salaryDetail?.dailyWages)}
                  </h4>
                </div>
              </div>
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="border rounded-3 p-3 bg-light text-center">
                  <h4 className="text-muted mb-1">Gross Salary</h4>
                  <h4 className="text-primary fw-bold">
                    {handleFormatAmount(grossIncome)}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 mb-5">
          <div className="card shadow-sm rounded-4">
            <div className="card-header bg-success text-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="bi bi-currency-rupee me-2"></i> Allowances
                Breakdown
              </h5>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle mb-0">
                <thead className="table-light text-center">
                  <tr>
                    {dataHead?.map((data) => {
                      if (data == 'Deductions') return;
                      return <th className="text-start">{data}</th>;
                    })}
                  </tr>
                </thead>

                <tbody
                  className={`text-center ${
                    pageStates?.isLoadingGetUserSalary ? 'loading-skeleton' : ''
                  }`}
                >
                  {salaryDetail?.earning?.length > 0 ? (
                    salaryDetail.earning.map((item, index) => (
                      <tr key={index}>
                        <td className="text-start fw-semibold text-dark">
                          {item?.sitesalaryhead?.salaryhead?.name || '--'}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isFixed
                                ? 'bg-success'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isFixed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isConditionBased
                                ? 'bg-info text-dark'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isConditionBased
                              ? 'Yes'
                              : 'No'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {(item?.sitesalaryhead?.rate &&
                              `${item?.sitesalaryhead?.rate}%`) ||
                              '--'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {item?.sitesalaryhead?.fixedBaseHead?.salaryhead
                              ?.name || '--'}
                          </span>
                        </td>
                        <td className="fw-bold text-end">
                          {handleFormatAmount(item?.amount ?? 0)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No Earning Found
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="table-light">
                  <tr>
                    <th className="text-start">Total</th>
                    <th colSpan={4}></th>
                    <th className="fw-bold text-end">
                      {handleFormatAmount(totalEarningsMonthly)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12 mb-5">
          <div className="card shadow-sm rounded-4">
            <div className="card-header bg-success text-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="bi bi-currency-rupee me-2"></i> Employer
                Contribution
              </h5>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle mb-0">
                <thead className="table-light text-center">
                  <tr>
                    {dataHead?.map((data) => {
                      if (data == 'Deductions') return;
                      return <th className="text-start">{data}</th>;
                    })}
                  </tr>
                </thead>

                <tbody
                  className={`text-center ${
                    pageStates?.isLoadingGetUserSalary ? 'loading-skeleton' : ''
                  }`}
                >
                  {salaryDetail?.fromEmployer?.length > 0 ? (
                    salaryDetail?.fromEmployer?.map((item, index) => (
                      <tr key={index}>
                        <td className="text-start fw-semibold text-dark">
                          {item?.sitesalaryhead?.salaryhead?.name || '--'}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isFixed
                                ? 'bg-success'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isFixed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isConditionBased
                                ? 'bg-info text-dark'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isConditionBased
                              ? 'Yes'
                              : 'No'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {(item?.sitesalaryhead?.rate &&
                              `${item?.sitesalaryhead?.rate}%`) ||
                              '--'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {item?.sitesalaryhead?.fixedBaseHead?.salaryhead
                              ?.name || '--'}
                          </span>
                        </td>
                        <td className="fw-bold text-end">
                          {handleFormatAmount(item?.amount ?? 0)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No Earning Found
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="table-light">
                  <tr>
                    <th className="text-start">Total</th>
                    <th colSpan={4}></th>
                    <th className="fw-bold text-end">
                      {handleFormatAmount(totalFromEmployerMonthly)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12 mb-5">
          <div className="card shadow-sm rounded-4">
            <div className="card-header bg-danger text-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="bi bi-dash-circle-fill me-2"></i> Deductions
                Breakdown
              </h5>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle mb-0">
                <thead className="table-light text-center">
                  <tr>
                    {dataHead?.map((data) => {
                      if (data == 'Allowances') return;
                      return <th className="text-start">{data}</th>;
                    })}
                  </tr>
                </thead>

                <tbody className="text-center">
                  {salaryDetail?.deduction?.length > 0 ? (
                    salaryDetail.deduction.map((item, index) => (
                      <tr key={index}>
                        <td className="text-start">
                          <p className="mb-0 fw-semibold text-dark">
                            {item?.sitesalaryhead?.salaryhead?.name || '--'}
                          </p>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isFixed
                                ? 'bg-success'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isFixed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item?.sitesalaryhead?.isConditionBased
                                ? 'bg-info text-dark'
                                : 'bg-secondary'
                            }`}
                          >
                            {item?.sitesalaryhead?.isConditionBased
                              ? 'Yes'
                              : 'No'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {(item?.sitesalaryhead?.rate &&
                              `${item?.sitesalaryhead?.rate}%`) ||
                              '--'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {item?.sitesalaryhead?.fixedBaseHead?.salaryhead
                              ?.name || '--'}
                          </span>
                        </td>
                        <td className="fw-bold text-end">
                          {handleFormatAmount(item?.amount ?? 0)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No Deduction Found
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="table-light">
                  <tr>
                    <th className="text-start">Total</th>
                    <th colSpan={4}></th>
                    <th className="fw-bold text-end">
                      {handleFormatAmount(totalDeductionsMonthly)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card shadow-sm rounded-4">
            <div className="card-header bg-primary text-white d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="bi bi-cash-coin me-2"></i> Salary Breakdown
              </h5>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle mb-0">
                <thead className="table-light text-center">
                  <tr>
                    <th className="text-start">Label</th>
                    <th>Description</th>
                    <th className="text-end">Monthly Amount (₹)</th>
                    <th className="text-end">Yearly Amount (₹)</th>
                  </tr>
                </thead>

                <tbody className="text-center">
                  <tr>
                    <td className="fw-semibold text-start">Gross Income</td>
                    <td className="text-muted">
                      Basic (
                      <span className="badge bg-info text-dark">
                        {handleFormatAmount(basicSalary)}
                      </span>
                      ) + Allowances (
                      <span className="badge bg-warning text-dark">
                        {handleFormatAmount(grossAllowances)}
                      </span>
                      )
                    </td>
                    <td className="fw-bold text-success text-end">
                      {handleFormatAmount(grossIncome)}
                    </td>
                    <td className="fw-bold text-success text-end">
                      {handleFormatAmount(grossIncome * 12)}
                    </td>
                  </tr>

                  <tr>
                    <td className="fw-semibold text-start">Total Deductions</td>
                    <td className="text-muted">From Deductions table</td>
                    <td className="fw-bold text-danger text-end">
                      {handleFormatAmount(totalDeductionsMonthly)}
                    </td>
                    <td className="fw-bold text-danger text-end">
                      {handleFormatAmount(totalDeductionsMonthly * 12)}
                    </td>
                  </tr>

                  <tr>
                    <td className="fw-semibold text-start">Net Pay</td>
                    <td className="text-muted">Gross Income - Deductions</td>
                    <td className="fw-bold text-primary text-end">
                      {handleFormatAmount(netSalary)}
                    </td>
                    <td className="fw-bold text-primary text-end">
                      {handleFormatAmount(netSalary * 12)}
                    </td>
                  </tr>

                  <tr className="table-primary">
                    <td className="fw-semibold text-start">CTC</td>
                    <td className="text-muted">
                      Gross Income + Employer Contribution
                    </td>
                    <td className="fw-bold text-end">
                      {handleFormatAmount(ctc)}
                    </td>
                    <td className="fw-bold text-end">
                      {handleFormatAmount(ctc * 12)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-cente justify-content-end gap-3 mt-4 mb-3">
        <div className="d-flex align-items-cente justify-content-end gap-3">
          <Button
            isLoading={pageStates.isLoadingOfferLetter}
            type="button"
            className="btn btn-primary px-5"
            onClick={handleGenerateOfferLetter}
          >
            Generate Offer Letter
          </Button>
        </div>
      </div>
    </>
  );
};
export default SalaryDetailsAndEdit;
